import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { Star, MapPin, Clock3, BadgeCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVATAR_COLORS = ["#003087", "#00AEEF", "#00AEEF", "#0E7C5B"];

const initials = (person) =>
  person.replace(/[^A-Za-zÀ-ÿ .]/g, "").split(" ").filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const FILTERS = ["all", "mutui", "prestiti", "errori", "pmi"];

function categorize(c) {
  const s = `${c.headline} ${c.detail}`.toLowerCase();
  if (/mutuo|surroga|rogito/.test(s)) return "mutui";
  if (/pmi|azienda|societ|punto vendita|macchinari|capannone|studio professionale|trasporti/.test(s)) return "pmi";
  if (/errore|omonimo|notificat|truffa|iban|duplicat|disguido/.test(s)) return "errori";
  return "prestiti";
}

export default function Cases() {
  const { t } = useLang();
  const [cases, setCases] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetch(`${API}/cases`).then((r) => r.json()).then(setCases).catch(() => {});
  }, []);

  const shown = useMemo(
    () => (filter === "all" ? cases : cases.filter((c) => categorize(c) === filter)),
    [cases, filter]
  );

  return (
    <section id="casi" className="py-20 lg:py-28" data-testid="cases-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00AEEF]">{t("cases.kicker")}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#003087] mt-3">{t("cases.title")}</h2>
          <p className="mt-4 text-sm text-[#64748B]">{t("cases.sub")}</p>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="testimonials-filter-category-select">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`case-filter-${f}`} onClick={() => setFilter(f)}
              className={`text-xs font-semibold rounded-full px-4 py-2 border transition-colors duration-200 ${filter === f ? "bg-[#00AEEF] border-[#00AEEF] text-white" : "border-[#E2E8F0] bg-white text-[#1A1A1A] hover:border-[#00AEEF]/50"}`}>
              {t(`cases.f.${f}`)}
            </button>
          ))}
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {shown.map((c, i) => (
            <motion.article
              key={c.case_id}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="card rounded-3xl p-6 flex flex-col"
              data-testid={`testimonial-case-card-${c.case_id.toLowerCase()}`}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center font-extrabold text-white text-sm shrink-0"
                  style={{ backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                  aria-hidden="true"
                >
                  {initials(c.person)}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#003087] text-sm">{c.person}, {c.age} anni</p>
                  <p className="text-[11px] text-[#64748B] flex items-center gap-1 mt-0.5"><MapPin size={10} /> {c.city}</p>
                </div>
                <div className="flex gap-0.5">
                  {Array.from({ length: c.stars }).map((_, s) => (
                    <Star key={s} size={12} className="text-[#00AEEF]" fill="#00AEEF" />
                  ))}
                </div>
              </div>

              <h3 className="mt-4 font-bold text-[#003087] text-[15px] leading-snug">{c.headline}</h3>
              <p className="mt-2 text-xs text-[#64748B] leading-relaxed flex-1">{c.detail}</p>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide bg-[#00AEEF]/10 text-[#00AEEF] rounded-full px-3 py-1.5">
                  <BadgeCheck size={11} /> {c.amount}
                </span>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#00AEEF] bg-[#E6F6FD] rounded-full px-3 py-1.5">
                  <Clock3 size={11} /> {c.days} {t("cases.days")}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[10px] font-semibold text-[#94A3B8] uppercase tracking-wide">{c.database}</span>
                <span className="text-[10px] text-[#CBD5E1]">{t("cases.demo")}</span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
